import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Minus, Plus, ShoppingCart } from 'lucide-react';
import {
  CatalogFormulaDto,
  CatalogItemDto,
  createOrder,
  fetchCatalog,
} from '../api/candidate';

const eur = (n: number) =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

function Stepper({
  qty,
  onChange,
}: {
  qty: number;
  onChange: (n: number) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(0, qty - 1))}
        className="w-8 h-8 rounded-lg border border-slate-300 text-slate-600 flex items-center justify-center hover:bg-slate-50 disabled:opacity-40"
        disabled={qty === 0}
        aria-label="Retirer"
      >
        <Minus size={15} />
      </button>
      <span className="w-6 text-center text-sm font-semibold text-slate-900">
        {qty}
      </span>
      <button
        type="button"
        onClick={() => onChange(qty + 1)}
        className="w-8 h-8 rounded-lg border border-slate-300 text-slate-600 flex items-center justify-center hover:bg-slate-50"
        aria-label="Ajouter"
      >
        <Plus size={15} />
      </button>
    </div>
  );
}

export default function CommandView({
  onOrdered,
}: {
  onOrdered?: () => void;
}) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['candidate', 'catalog'],
    queryFn: fetchCatalog,
  });

  // Quantités par identifiant (clé préfixée pour distinguer article/formule).
  const [qty, setQty] = useState<Record<string, number>>({});
  const [done, setDone] = useState<{ number: string; total: number } | null>(
    null,
  );

  const setItemQty = (key: string, n: number) =>
    setQty((prev) => ({ ...prev, [key]: n }));

  const cartTotal = useMemo(() => {
    if (!data) return 0;
    let total = 0;
    for (const it of data.items)
      total += (qty[`item:${it.id}`] ?? 0) * it.priceInclTax;
    for (const f of data.formulas)
      total += (qty[`formula:${f.id}`] ?? 0) * f.priceInclTax;
    return total;
  }, [data, qty]);

  const lineCount = Object.values(qty).filter((n) => n > 0).length;

  const orderMutation = useMutation({
    mutationFn: () => {
      if (!data) throw new Error('Catalogue indisponible.');
      const items = data.items
        .map((it) => ({
          catalogItemId: it.id,
          quantity: qty[`item:${it.id}`] ?? 0,
        }))
        .filter((l) => l.quantity > 0);
      const formulas = data.formulas
        .map((f) => ({
          catalogFormulaId: f.id,
          quantity: qty[`formula:${f.id}`] ?? 0,
        }))
        .filter((l) => l.quantity > 0);
      return createOrder({ items, formulas });
    },
    onSuccess: (res) => {
      // Paiement avant émission de la facture : on redirige vers Stripe Checkout.
      if (res.requiresPayment && res.checkoutUrl) {
        window.location.href = res.checkoutUrl;
        return;
      }
      // Repli (paiement en ligne non activé) : facture « envoyée » à régler plus tard.
      setDone({ number: res.invoiceNumber ?? '', total: res.totalTtc });
      setQty({});
      void queryClient.invalidateQueries({
        queryKey: ['candidate', 'payable-invoices'],
      });
    },
    onError: (e: Error) =>
      window.alert(e.message || 'Commande impossible pour le moment.'),
  });

  if (isLoading) {
    return <p className="text-slate-500">Chargement du catalogue…</p>;
  }
  if (isError || !data) {
    return (
      <p className="text-red-600">Impossible de charger le catalogue.</p>
    );
  }

  if (done) {
    return (
      <div className="max-w-lg mx-auto text-center bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
        <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
          <CheckCircle2 size={30} />
        </div>
        <h2 className="text-slate-900 font-semibold text-lg">
          Commande enregistrée
        </h2>
        <p className="text-sm text-slate-600 mt-2">
          Votre commande de <b>{eur(done.total)}</b>
          {done.number ? (
            <> (facture <b>{done.number}</b>)</>
          ) : null}{' '}
          a bien été enregistrée. Votre auto-école reviendra vers vous pour le
          règlement.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={() => onOrdered?.()}
            className="px-4 py-2.5 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors"
          >
            Voir mes paiements
          </button>
          <button
            onClick={() => setDone(null)}
            className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-50 transition-colors"
          >
            Commander à nouveau
          </button>
        </div>
      </div>
    );
  }

  const hasCatalog = data.items.length > 0 || data.formulas.length > 0;

  return (
    <section className="space-y-6 pb-28">
      <div>
        <h1 className="text-slate-900">Commander</h1>
        <p className="text-slate-600">
          Choisissez vos prestations : une facture est générée immédiatement,
          réglable en ligne.
        </p>
      </div>

      {!hasCatalog && (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500">
          Le catalogue de votre auto-école n'est pas encore disponible.
        </div>
      )}

      {data.formulas.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h2 className="text-slate-900 mb-4">Formules</h2>
          <div className="divide-y divide-slate-100">
            {data.formulas.map((f: CatalogFormulaDto) => (
              <div key={f.id} className="flex items-center gap-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">
                    {f.name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {eur(f.priceInclTax)} TTC · {f.itemsCount} article(s)
                  </div>
                </div>
                <Stepper
                  qty={qty[`formula:${f.id}`] ?? 0}
                  onChange={(n) => setItemQty(`formula:${f.id}`, n)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {data.items.length > 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm">
          <h2 className="text-slate-900 mb-4">Articles</h2>
          <div className="divide-y divide-slate-100">
            {data.items.map((it: CatalogItemDto) => (
              <div key={it.id} className="flex items-center gap-4 py-3">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900">
                    {it.name}
                  </div>
                  <div className="text-xs text-slate-500">
                    {eur(it.priceInclTax)} TTC
                    {it.hours > 0 ? ` · ${it.hours} h` : ''}
                  </div>
                </div>
                <Stepper
                  qty={qty[`item:${it.id}`] ?? 0}
                  onChange={(n) => setItemQty(`item:${it.id}`, n)}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Barre de panier fixe */}
      {lineCount > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 shadow-[0_-4px_16px_rgba(0,0,0,0.06)]">
          <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-4">
            <div className="flex-1">
              <div className="text-xs text-slate-500">
                {lineCount} ligne(s)
              </div>
              <div className="text-lg font-bold text-slate-900">
                {eur(cartTotal)}
              </div>
            </div>
            <button
              onClick={() => orderMutation.mutate()}
              disabled={orderMutation.isPending}
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition-colors disabled:opacity-60"
            >
              <ShoppingCart size={18} />
              {orderMutation.isPending ? 'Validation…' : 'Valider ma commande'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
